import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ActivityIndicator,
  TextInput,
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
  ScrollView,
} from "react-native";
import { Colors } from "@/constants";
import { ms } from "@/ui/moderateScale";
import Icon from "@/ui/Icon";
import { useTranslation } from "react-i18next";
import { App } from "@capacitor/app";
import { useUISlice } from "@/models/useUISlice";
import { useWidgetSearch } from "@/models/useWidgetSearch";
import { useRouter } from "@/_router";
import { useEffect, useState } from "react";
import { SearchResultItem } from "@/models/types";
import { getBasePath } from "@/models/utils";
import { getPlatform } from "@/models/platform";

const AlertWebWidgetSearch = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const params = useUISlice((state) => state.alertWidgetSearch);
  const visible = Boolean(params);
  const setVisible = useUISlice((state) => state.setAlertWidgetSearch);
  const width = useUISlice((state) => state.device.width);
  const noteId = params?.noteId;
  const folderId = params?.folderId;
  const placeholder = params?.placeholder;
  const [showSuggestion, setShowSuggestion] = useState(false);

  const {
    searchText,
    loading,
    setSearchText,
    results,
    setResults,
    search,
    itemsProcessed,
    nextToken,
    clearSearch,
    setItemsProcessed,
    setNextToken,
  } = useWidgetSearch(noteId, folderId);

  // Track the last searched text to detect when user is typing a new query
  const [lastSearchedText, setLastSearchedText] = useState("");

  useEffect(() => {
    const listenBackButton = () => {
      App.addListener("backButton", () => {
        setVisible(undefined);
      });
    };

    if (visible) {
      listenBackButton();

      setSearchText("");
      setShowSuggestion(false);
      setResults([]);
      setLastSearchedText("");
      setItemsProcessed(0);
      setNextToken(undefined);
    }

    return () => {
      App.removeAllListeners();
    };
  }, [visible]);

  useEffect(() => {
    if (visible && getPlatform() === "web") {
      // Apply the overlay color to the theme-color meta tag for web
      const metaThemeColor = document.querySelector("meta[name=theme-color]");
      if (metaThemeColor) {
        metaThemeColor.setAttribute("content", "#929295");
      }

      return () => {
        if (metaThemeColor) {
          metaThemeColor.setAttribute("content", "");
        }
      };
    }
  }, [visible]);

  useEffect(() => {
    // Show suggestion when user types, but hide it when there are results or when search has been performed
    const hasSearchTerm = searchText.trim().length > 0;
    const noResults = results.length === 0;
    const isTypingNewQuery = searchText.trim() !== lastSearchedText;

    // Show suggestions when:
    // 1. There's a search term AND
    // 2. Either there are no results OR the user is typing something different from last search
    setShowSuggestion(hasSearchTerm && (noResults || isTypingNewQuery));
  }, [searchText, results.length, lastSearchedText]);

  if (!visible) return null;

  const handleResultClick = (result: SearchResultItem) => {
    setVisible(undefined);
    if (result.textStart === -1) {
      router.push(`${getBasePath()}/note?id=${result.noteId}`);
    } else {
      router.push(
        `${getBasePath()}/note?id=${result.noteId}&tab=transcript&segmentStart=${result.textStart}&segmentText=${encodeURIComponent(result.text)}`
      );
    }
  };

  const handleKeyPress = (
    e: NativeSyntheticEvent<TextInputKeyPressEventData>
  ) => {
    if (e.nativeEvent.key === "Enter" && searchText.trim()) {
      search({ needle: searchText.trim(), itemsProcessed, nextToken });
      setLastSearchedText(searchText.trim());
      setShowSuggestion(false);
    }
  };

  const handleSearchClick = () => {
    if (searchText.trim()) {
      search({ needle: searchText.trim(), itemsProcessed, nextToken });
      setLastSearchedText(searchText.trim());
      setShowSuggestion(false);
    }
  };

  const renderHighlightedText = (text: string) => {
    return text.split(/<mark>(.*?)<\/mark>/).map((part, i) => (
      <Text key={i} style={i % 2 === 1 ? s.highlight : undefined}>
        {part}
      </Text>
    ));
  };

  const renderSearchResult = (result: SearchResultItem, index: number) => (
    <Pressable
      key={`item-${index}`}
      style={s.resultItem}
      onPress={() => handleResultClick(result)}
    >
      <Text numberOfLines={1} style={s.resultTitle}>
        {renderHighlightedText(result.noteName)}
      </Text>
      <Text numberOfLines={3} style={s.resultDescription}>
        {(() => {
          const parts = result.text.split(/<mark>(.*?)<\/mark>/);
          if (parts.length > 1) {
            return parts.map((part, i) => {
              if (i % 2 === 1) {
                const prevContext = parts[i - 1]?.slice(-100) || "";
                const nextContext = parts[i + 1]?.slice(0, 100) || "";
                return (
                  <Text key={i}>
                    {prevContext}
                    <Text style={s.highlight}>{part}</Text>
                    {nextContext}
                    {i < parts.length - 2 ? " ... " : ""}
                  </Text>
                );
              }
              return null;
            });
          }
          return result.text;
        })()}
      </Text>
    </Pressable>
  );

  return (
    <>
      <Pressable
        style={s.modalBackdrop}
        onPress={() => setVisible(undefined)}
      />
      <View
        style={[s.searchModal, { width: Math.min(width - ms(32), ms(580)) }]}
      >
        <View style={s.searchContainer}>
          <View style={s.searchHeader}>
            <View style={s.searchInputWrapper}>
              <Icon icon="searchBar" style={s.searchIcon} />
              <TextInput
                autoFocus={true}
                value={searchText}
                onChangeText={(text) => {
                  setSearchText(text);
                  // No longer triggering search on text change
                }}
                onKeyPress={handleKeyPress}
                placeholder={placeholder || t("alertWidgetSearch.placeholder")}
                placeholderTextColor={Colors.gray6}
                style={s.searchInput}
              />
              {searchText.length > 0 && (
                <Pressable onPress={clearSearch} style={s.clearButton}>
                  <Icon icon="close" style={s.clearIcon} />
                </Pressable>
              )}
            </View>
          </View>

          {loading && (
            <View style={s.loadingContainer}>
              <ActivityIndicator size="small" color={Colors.accent} />
              <Text style={s.loadingText}>
                {t("alertWidgetSearch.searching")}
              </Text>
            </View>
          )}

          {!loading && showSuggestion && searchText.trim() && (
            <Pressable style={s.suggestionItem} onPress={handleSearchClick}>
              <Icon icon="searchBar" style={s.suggestionIcon} />
              <Text style={s.suggestionText}>
                {t("alertWidgetSearch.searchFor", {
                  query: searchText.trim(),
                })}
              </Text>
            </Pressable>
          )}

          {!loading && (
            <ScrollView style={s.resultsList}>
              {results.length > 0 ? (
                results.map((item, index) => renderSearchResult(item, index))
              ) : (
                <View style={s.emptyContainer}>
                  {!showSuggestion && !loading && (
                    <Text style={s.emptyText}>
                      {t("alertWidgetSearch.empty")}
                    </Text>
                  )}
                </View>
              )}
            </ScrollView>
          )}
        </View>
      </View>
    </>
  );
};

const s = StyleSheet.create({
  modalBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    backdropFilter: "blur(5px)",
    zIndex: 1000,
  } as any,
  searchModal: {
    position: "fixed",
    top: "20%",
    left: "50%",
    transform: "translate(-50%, 0)",
    width: ms(580),
    height: ms(400),
    backgroundColor: "rgba(255, 255, 255, 0.98)",
    backdropFilter: "blur(5px)",
    zIndex: 1001,
    borderRadius: ms(12),
    boxShadow: "0 0 0 1px rgba(0,0,0,0.05), 0 20px 40px rgba(0,0,0,0.15)",
  } as any,
  searchContainer: {
    flex: 1,
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    borderRadius: ms(12),
  } as any,
  searchHeader: {
    padding: ms(8),
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0,0,0,0.1)",
  },
  searchInputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "transparent",
    borderRadius: ms(6),
    padding: ms(4),
  },
  searchIcon: {
    width: ms(16),
    height: ms(16),
    tintColor: Colors.gray6,
    marginLeft: ms(8),
  },
  searchInput: {
    flex: 1,
    fontSize: ms(16),
    color: Colors.text,
    paddingHorizontal: ms(8),
    paddingVertical: ms(6),
    backgroundColor: "transparent",
    border: "none",
    outlineStyle: "none",
  } as any,
  clearButton: {
    padding: ms(4),
    marginRight: ms(4),
    cursor: "pointer",
  } as any,
  clearIcon: {
    width: ms(16),
    height: ms(16),
    tintColor: Colors.gray6,
  },
  suggestionItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: ms(12),
    margin: ms(4),
    marginHorizontal: ms(8),
    borderRadius: ms(6),
    cursor: "pointer",
    backgroundColor: "rgba(0,0,0,0.03)",
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.05)",
    },
  } as any,
  suggestionIcon: {
    width: ms(16),
    height: ms(16),
    tintColor: Colors.gray6,
    marginRight: ms(8),
  },
  suggestionText: {
    fontSize: ms(14),
    color: Colors.text,
  },
  resultsList: {
    flex: 1,
    overflowY: "auto",
    WebkitScrollbarWidth: "6px",
    WebkitScrollbarThumb: {
      backgroundColor: "rgba(0,0,0,0.2)",
      borderRadius: "3px",
    },
  } as any,
  resultItem: {
    padding: ms(12),
    margin: ms(4),
    marginHorizontal: ms(8),
    borderRadius: ms(6),
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.05)",
    },
  } as any,
  resultTitle: {
    fontSize: ms(14),
    fontWeight: "500",
    color: Colors.text,
    marginBottom: ms(2),
  },
  resultDescription: {
    fontSize: ms(12),
    color: Colors.gray6,
    lineHeight: ms(18),
  },
  highlight: {
    backgroundColor: "rgba(255, 213, 0, 0.4)",
    borderRadius: ms(2),
    padding: ms(2),
  },
  emptyContainer: {
    padding: ms(32),
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    fontSize: ms(16),
    color: Colors.gray6,
  },
  loadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: ms(12),
    marginVertical: ms(8),
  },
  loadingText: {
    fontSize: ms(14),
    color: Colors.gray6,
    marginLeft: ms(8),
  },
}) as any;

// Add keyframe animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
    @keyframes spotlightFadeIn {
      from {
        opacity: 0;
        transform: translate(-50%, -10px);
      }
      to {
        opacity: 1;
        transform: translate(-50%, 0);
      }
    }
  `;
document.head.appendChild(styleSheet);

export default AlertWebWidgetSearch;
